import { Link } from 'react-router-dom';
import { Star, Lock, Sparkles } from 'lucide-react';
import { ReadingTime } from './ReadingTime';
import { PremiumCardOverlay } from './PremiumLock';

export default function StoryCard({ story, size = 'default', showAuthorLink = false, showOfficialBadge = false }) {
  const sizeClasses = {
    mobile: 'w-[140px]',
    small: 'w-[140px] sm:w-36',
    default: 'w-[140px] sm:w-40 md:w-44 lg:w-48',
    large: 'w-48 sm:w-56'
  };

  const destination = `/story/${story.slug || story.id}`;
  const isPremium = !!(story.is_premium || story.is_locked);

  return (
    <Link
      to={destination}
      data-testid={`story-card-${story.id}`}
      className={`${sizeClasses[size]} flex-shrink-0 group active:scale-[0.98] transition-transform`}
    >
      <div className="relative aspect-[2/3] rounded-xl overflow-hidden shadow-lg bg-surface">
        {/* Cover Image */}
        <img
          src={story.cover_image || 'https://images.unsplash.com/photo-1605547446826-efe6b45ffe4d?w=400'}
          alt={story.title}
          className="w-full h-full object-cover"
          loading="lazy"
        />

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />

        {/* Access Badge — gold Premium when locked, green "Grátis" otherwise.
            Both badges share the same anchor + sizing so the layout stays
            stable when a story toggles between free/paid in the CMS. */}
        {isPremium ? (
          <div
            data-testid={`story-badge-premium-${story.id}`}
            className="absolute top-2 right-2 z-20 bg-gradient-to-r from-gold to-gold-light text-foreground text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow-md"
          >
            <Lock className="w-2.5 h-2.5" />
            <span className="hidden sm:inline">Premium</span>
          </div>
        ) : (
          <div
            data-testid={`story-badge-free-${story.id}`}
            className="absolute top-2 right-2 z-20 bg-emerald-500/95 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow-md"
          >
            <Sparkles className="w-2.5 h-2.5" />
            <span className="hidden sm:inline">Grátis</span>
          </div>
        )}

        {/* Locked overlay */}
        {story.is_locked && <PremiumCardOverlay />}

        {/* Official Intimate badge — top-left, gold pill. Only rendered when
            the caller opts in AND the story is authored by an official/admin
            account. Positioned so it never overlaps the Premium/Grátis badge
            in the top-right corner. */}
        {showOfficialBadge && story.is_official_author && (
          <div
            data-testid={`story-badge-official-${story.id}`}
            className="absolute top-2 left-2 z-20 bg-gradient-to-r from-gold to-gold-light text-black text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow-md ring-1 ring-gold/40"
            title="Conto original Intimate"
          >
            <Sparkles className="w-2.5 h-2.5" />
            <span>Intimate</span>
          </div>
        )}

        {/* Content */}
        <div className="absolute bottom-0 left-0 right-0 p-3">
          <h3 className="font-brand text-foreground font-semibold text-sm leading-tight line-clamp-2 mb-1">
            {story.title}
          </h3>
          {showAuthorLink && story.author_id ? (
            <p className="text-muted-foreground text-xs line-clamp-1 mb-2">
              por{' '}
              <span
                role="link"
                tabIndex={0}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  window.location.href = `/autor/${story.author_id}`;
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    window.location.href = `/autor/${story.author_id}`;
                  }
                }}
                className="text-ruby-light hover:underline cursor-pointer"
                data-testid={`story-card-author-${story.id}`}
              >
                {story.author_name}
              </span>
            </p>
          ) : (
            <p className="text-muted-foreground text-xs line-clamp-1 mb-2">
              {story.author_name}
            </p>
          )}

          {/* Meta */}
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <ReadingTime minutes={story.reading_time} size="small" className="text-muted-foreground" />
            {story.rating > 0 && (
              <span className="flex items-center gap-0.5">
                <Star className="w-3 h-3 text-gold fill-gold" />
                {story.rating.toFixed(1)}
              </span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}
